'use client';

import React, { useState } from 'react';
import { Breadcrumbs } from '@/components/Navigation/Breadcrumbs';
import { FAQSection } from '@/components/Content/FAQSection';
import { RelatedContent } from '@/components/Content/RelatedContent';
import { SchemaMarkup } from '@/components/Schema/SchemaMarkup';
import { HybridSystemModal } from '@/components/tools/mortgage-calculator/components/HybridSystemModal';
import { Lightbulb, ArrowRight } from 'lucide-react';
import type { ProcessedContent } from '@/lib/content';

interface ToolPageTemplateProps {
  content: ProcessedContent;
  breadcrumbs: Array<{ name: string; url: string }>;
  fullSlug: string;
}

/**
 * ToolPageTemplate - For interactive tools like calculators, analyzers, etc.
 * 
 * Key features:
 * - Optional H1/subheadline (if present in frontmatter, renders for SEO)
 * - No hero CTAs or social proof
 * - Clean, minimal layout focused on the tool
 * - Optional FAQs and related content at bottom
 */
export const ToolPageTemplate: React.FC<ToolPageTemplateProps> = ({ 
  content, 
  breadcrumbs,
  fullSlug 
}) => {
  const { meta, compiledContent } = content;
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Check if we should show server-rendered header
  const showHeader = !!(meta.h1 || meta.subHeadline);
  
  // Only show modal trigger for the ROI calculator page
  const isROICalculator = fullSlug === 'mortgage-marketing-roi-calculator';

  // Determine schema types to render
  const schemaTypes = meta.schema || [];

  // Build effective schema list — auto-include FAQPage when faqs are present
  const effectiveSchema = (() => {
    const s = [...schemaTypes];
    if (meta.faqs && meta.faqs.length >= 2 && !s.includes('FAQPage')) {
      s.push('FAQPage');
    }
    return s;
  })();

  return (
    <>
      {/* Schema Markup */}
      {effectiveSchema
        .filter((schemaType) => schemaType !== 'Organization' && schemaType !== 'LocalBusiness')
        .map((schemaType) => {
        return (
          <SchemaMarkup
            key={schemaType}
            type={schemaType as any}
            data={{
              title: meta.title,
              description: meta.description,
              url: `/${fullSlug}`,
              image: meta.image || meta.images?.og,
              publishedTime: meta.publishedTime,
              modifiedTime: meta.modifiedTime,
              author: meta.author,
              breadcrumbs: breadcrumbs,
              faqs: meta.faqs
            }}
          />
        );
      })}

    <div className="min-h-screen bg-white">
      {/* Breadcrumbs - always show */}
      {breadcrumbs.length > 0 && (
        <div className="w-full bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <Breadcrumbs items={breadcrumbs} />
          </div>
        </div>
      )}
      
      {/* Optional Header - only if h1 or subHeadline exist */}
      {showHeader && (
        <div className="bg-white py-8 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {meta.h1 && (
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {meta.h1 || meta.title}
              </h1>
            )}
            <div className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Model two scenarios: Paid ads in isolation or paid ads that activate agent partnerships, client referrals, database reactivation, and brand/organic growth.
            </div>
            {/* Modal trigger card - only for ROI calculator */}
            {isROICalculator && (
              <div className="mt-8 max-w-2xl mx-auto">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="group w-full bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                        <Lightbulb className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-base font-semibold text-gray-900 mb-1">
                        How does this calculator work?
                      </div>
                      <div className="text-sm text-gray-600">
                        Understand the Hybrid System™ methodology behind our calculations
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Main Content - tool/calculator goes here */}
      <div className={`bg-gray-50 ${showHeader ? "pt-12 pb-16" : "pt-8 pb-16"}`}>
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Render the MDX content which includes the tool */}
          {compiledContent}
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Optional FAQ Section */}
          {meta.faqs && meta.faqs.length > 0 && (
            <div className="mt-12">
              <FAQSection faqs={meta.faqs} />
            </div>
          )}
          
          {/* Optional Related Content */}
          {meta.relatedPages && meta.relatedPages.length > 0 && (
            <div className="mt-12">
              <RelatedContent pages={meta.relatedPages} />
            </div>
          )}
        </div>
      </div>
      
      {/* Hybrid System Modal - only for ROI calculator */}
      {isROICalculator && (
        <HybridSystemModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
    </>
  );
};